import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  getMovieDetails, 
  getMovieVideos, 
  getSimilarMovies, 
  getMovieCredits,
  getImageUrl,
  formatRuntime,
  formatMoney,
  formatDate
} from '../lib/tmdb';
import { 
  isFavorite, 
  addToFavorites, 
  removeFromFavorites,
  isInWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  isWatched,
  markAsWatched,
  removeFromWatched,
  getUserRating,
  setUserRating,
  getCollections,
  addMovieToCollection
} from '../lib/storage';
import CardMovie from './CardMovie';
import LoadingSpinner from './LoadingSpinner';

const MovieDetails = ({ onMovieUpdate }) => {
  const params = useParams();
  const [movie, setMovie] = useState({});
  const [videos, setVideos] = useState([]);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [credits, setCredits] = useState({ cast: [], crew: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // User interaction states
  const [isFav, setIsFav] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watched, setWatched] = useState(false);
  const [userRating, setUserRatingState] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collections, setCollections] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  // Load movie data
  useEffect(() => {
    if (params.id) {
      loadMovieData();
    }
  }, [params.id]);

  // Update user states when movie changes
  useEffect(() => {
    if (movie.id) {
      setIsFav(isFavorite(movie.id));
      setInWatchlist(isInWatchlist(movie.id));
      setWatched(isWatched(movie.id));
      setUserRatingState(getUserRating(movie.id));
      setCollections(getCollections());
    }
  }, [movie.id]);

  const loadMovieData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all movie data concurrently
      const [
        movieResult,
        videosResult,
        similarResult,
        creditsResult
      ] = await Promise.allSettled([
        getMovieDetails(params.id),
        getMovieVideos(params.id),
        getSimilarMovies(params.id),
        getMovieCredits(params.id)
      ]);

      if (movieResult.status === 'fulfilled') {
        setMovie(movieResult.value);
      } else {
        setError('Movie not found or unavailable');
      }

      if (videosResult.status === 'fulfilled') {
        setVideos(videosResult.value.slice(0, 5)); // Limit to 5 videos
      }

      if (similarResult.status === 'fulfilled') {
        setSimilarMovies(similarResult.value);
      }

      if (creditsResult.status === 'fulfilled') {
        setCredits(creditsResult.value);
      }

    } catch (err) {
      setError(err.message || 'Failed to load movie details');
    } finally {
      setLoading(false);
    }
  };

  // User action handlers
  const handleFavoriteToggle = async () => {
    setIsUpdating(true);
    try {
      if (isFav) {
        removeFromFavorites(movie.id);
        setIsFav(false);
      } else {
        addToFavorites(movie);
        setIsFav(true);
      }
      onMovieUpdate?.();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWatchlistToggle = async () => {
    setIsUpdating(true);
    try {
      if (inWatchlist) {
        removeFromWatchlist(movie.id);
        setInWatchlist(false);
      } else {
        addToWatchlist(movie);
        setInWatchlist(true);
      }
      onMovieUpdate?.();
    } catch (error) {
      console.error('Error toggling watchlist:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWatchedToggle = async () => {
    setIsUpdating(true);
    try {
      if (watched) {
        removeFromWatched(movie.id);
        setWatched(false);
        setUserRatingState(null);
      } else {
        markAsWatched(movie);
        setWatched(true);
        if (inWatchlist) {
          removeFromWatchlist(movie.id);
          setInWatchlist(false);
        }
      }
      onMovieUpdate?.();
    } catch (error) {
      console.error('Error toggling watched:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRating = async (rating) => {
    setIsUpdating(true);
    try {
      if (rating === null) {
        setUserRating(movie.id, null);
        setUserRatingState(null);
      } else {
        setUserRating(movie.id, rating);
        setUserRatingState(rating);
        
        if (!watched) {
          markAsWatched(movie, rating);
          setWatched(true);
          if (inWatchlist) {
            removeFromWatchlist(movie.id);
            setInWatchlist(false);
          }
        }
      }
      setShowRatingModal(false);
      onMovieUpdate?.();
    } catch (error) {
      console.error('Error setting rating:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddToCollection = (collectionId) => {
    try {
      addMovieToCollection(collectionId, movie);
      setShowCollectionModal(false);
      onMovieUpdate?.();
    } catch (error) {
      console.error('Error adding to collection:', error);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner type="details" message="Loading movie details..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <div className="w-24 h-24 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.088 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Movie Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Link
            to="/Movies"
            className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span>Back to Movies</span>
          </Link>
        </div>
      </div>
    );
  }

  const backdropUrl = getImageUrl(movie.backdrop_path, 'w1280');
  const posterUrl = getImageUrl(movie.poster_path, 'w500');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      
      {/* Hero Section */}
      <div className="relative h-[70vh] lg:h-[80vh] overflow-hidden">
        {/* Backdrop Image */}
        {backdropUrl && (
          <div className="absolute inset-0">
            <img
              src={backdropUrl}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
          </div>
        )}

        {/* Back Button */}
        <Link
          to="/Movies"
          className="absolute top-6 left-6 z-20 flex items-center space-x-2 bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-black/70 transition-all duration-200 group"
        >
          <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          <span>Back to Movies</span>
        </Link>

        {/* Content */}
        <div className="absolute inset-0 flex items-end z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
            <div className="flex flex-col lg:flex-row items-end space-y-8 lg:space-y-0 lg:space-x-8">
              
              {/* Movie Poster */}
              <div className="flex-shrink-0">
                <div className="w-48 lg:w-64 aspect-[2/3] bg-gray-800 rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
                  {posterUrl ? (
                    <img
                      src={posterUrl}
                      alt={movie.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Movie Info */}
              <div className="flex-1 text-white">
                <h1 className="text-4xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
                  {movie.title}
                </h1>
                
                {movie.tagline && (
                  <p className="text-xl text-white/90 mb-4 italic">
                    "{movie.tagline}"
                  </p>
                )}

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 mb-6 text-white/90">
                  <div className="flex items-center space-x-1">
                    <svg className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <span className="font-semibold text-xl">{movie.vote_average?.toFixed(1)}</span>
                    <span className="text-white/70">({movie.vote_count?.toLocaleString()} votes)</span>
                  </div>
                  
                  {movie.release_date && (
                    <div className="flex items-center space-x-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                      </svg>
                      <span>{movie.release_date.split('-')[0]}</span>
                    </div>
                  )}
                  
                  {movie.runtime && (
                    <div className="flex items-center space-x-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      <span>{formatRuntime(movie.runtime)}</span>
                    </div>
                  )}

                  {/* User Rating */}
                  {userRating && (
                    <div className="flex items-center space-x-1 bg-yellow-500/90 backdrop-blur-sm px-3 py-1 rounded-full">
                      <svg className="w-4 h-4 text-white fill-current" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <span className="text-white font-semibold">Your Rating: {userRating}/10</span>
                    </div>
                  )}
                </div>

                {/* Genres */}
                {movie.genres && movie.genres.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {movie.genres.map((genre) => (
                      <span
                        key={genre.id}
                        className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium"
                      >
                        {genre.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-4">
                  
                  {/* Watch Trailer */}
                  {videos.length > 0 && (
                    <a
                      href={`https://www.youtube.com/watch?v=${videos[0].key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl"
                    >
                      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                      <span>Watch Trailer</span>
                    </a>
                  )}

                  {/* Favorite Button */}
                  <button
                    onClick={handleFavoriteToggle}
                    disabled={isUpdating}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-colors duration-200 ${
                      isFav
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-red-500/70'
                    }`}
                  >
                    <svg className={`w-5 h-5 ${isFav ? 'fill-current' : ''}`} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                    </svg>
                    <span>{isFav ? 'Remove from Favorites' : 'Add to Favorites'}</span>
                  </button>

                  {/* Watchlist Button */}
                  {!watched && (
                    <button
                      onClick={handleWatchlistToggle}
                      disabled={isUpdating}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-colors duration-200 ${
                        inWatchlist
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : 'bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-blue-500/70'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                      </svg>
                      <span>{inWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist'}</span>
                    </button>
                  )}

                  {/* More Actions */}
                  <div className="flex items-center space-x-2">
                    {/* Rate Movie */}
                    <button
                      onClick={() => setShowRatingModal(true)}
                      className="p-3 bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white rounded-xl hover:bg-white/30 transition-colors duration-200"
                      title={userRating ? `Your rating: ${userRating}/10` : 'Rate this movie'}
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </button>

                    {/* Add to Collection */}
                    <button
                      onClick={() => setShowCollectionModal(true)}
                      className="p-3 bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white rounded-xl hover:bg-white/30 transition-colors duration-200"
                      title="Add to collection"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </button>

                    {/* Mark as Watched */}
                    <button
                      onClick={handleWatchedToggle}
                      disabled={isUpdating}
                      className={`p-3 rounded-xl transition-colors duration-200 ${
                        watched
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-green-500/70'
                      }`}
                      title={watched ? 'Mark as unwatched' : 'Mark as watched'}
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-200 dark:bg-gray-800 rounded-xl p-1 mb-8">
          {[
            { id: 'overview', label: 'Overview', icon: '📖' },
            { id: 'cast', label: 'Cast & Crew', icon: '🎭' },
            { id: 'videos', label: 'Videos', icon: '🎬' },
            // { id: 'details', label: 'Details', icon: 'ℹ️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className=" bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Plot Summary</h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                  {movie.overview || 'No overview available for this movie.'}
                </p>
              </div>

              {/* Additional Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                    {movie.vote_average?.toFixed(1) || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">TMDB Rating</div>
                </div>
                
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                    {movie.vote_count?.toLocaleString() || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Votes</div>
                </div>
                
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                    {movie.runtime ? formatRuntime(movie.runtime) : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Runtime</div>
                </div>
                
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
                    {movie.release_date?.split('-')[0] || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Release Year</div>
                </div>
              </div>

              {/* Similar Movies */}
              {similarMovies && similarMovies.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Similar Movies</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {similarMovies.slice(0, 12).map((movie) => (
                      <CardMovie
                        key={movie.id}
                        movie={movie}
                        onUpdate={onMovieUpdate}
                        size="small"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cast & Crew Tab */}
          {activeTab === 'cast' && (
            <div className="space-y-8">
              {/* Cast */}
              {credits.cast && credits.cast.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Main Cast</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                    {credits.cast.slice(0, 12).map((person) => (
                      <div key={person.id} className="text-center group">
                        <div className="w-20 h-20 mx-auto mb-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden group-hover:ring-4 group-hover:ring-blue-500/30 transition-all duration-200">
                          {person.profile_path ? (
                            <img
                              src={getImageUrl(person.profile_path, 'w200')}
                              alt={person.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{person.name}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-xs">{person.character}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Crew */}
              {credits.crew && credits.crew.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Key Crew</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {credits.crew
                      .filter(person => ['Director', 'Producer', 'Executive Producer', 'Writer', 'Screenplay', 'Story', 'Director of Photography', 'Original Music Composer'].includes(person.job))
                      .slice(0, 12)
                      .map((person) => (
                        <div key={`${person.id}-${person.job}`} className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden flex-shrink-0">
                            {person.profile_path ? (
                              <img
                                src={getImageUrl(person.profile_path, 'w200')}
                                alt={person.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{person.name}</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-xs">{person.job}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Videos & Trailers</h2>
              {videos && videos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {videos.map((video) => (
                    <div key={video.id} className="bg-gray-50 dark:bg-gray-700 rounded-xl overflow-hidden">
                      <div className="aspect-video bg-gray-900 relative group cursor-pointer">
                        <img
                          src={`https://img.youtube.com/vi/${video.key}/hqdefault.jpg`}
                          alt={video.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition-colors duration-200">
                          <a
                            href={`https://www.youtube.com/watch?v=${video.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-16 h-16 bg-red-600 hover:bg-red-700 rounded-full transition-colors duration-200"
                          >
                            <svg className="w-6 h-6 text-white fill-current ml-1" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </a>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{video.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                          <span className="capitalize">{video.type}</span>
                          <span>•</span>
                          <span className="capitalize">{video.site}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                  <p className="text-gray-600 dark:text-gray-400">No videos available for this movie</p>
                </div>
              )}
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Movie Details</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    Basic Information
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Original Title:</span>
                      <span className="text-gray-900 dark:text-white text-right max-w-xs">{movie.original_title || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Release Date:</span>
                      <span className="text-gray-900 dark:text-white">{movie.release_date ? formatDate(movie.release_date) : 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Runtime:</span>
                      <span className="text-gray-900 dark:text-white">{movie.runtime ? formatRuntime(movie.runtime) : 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Status:</span>
                      <span className="text-gray-900 dark:text-white">{movie.status || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Original Language:</span>
                      <span className="text-gray-900 dark:text-white uppercase">{movie.original_language || 'N/A'}</span>
                    </div>

                    {movie.spoken_languages && movie.spoken_languages.length > 0 && (
                      <div className="flex justify-between items-start">
                        <span className="text-gray-600 dark:text-gray-400 font-medium">Languages:</span>
                        <div className="text-gray-900 dark:text-white text-right max-w-xs">
                          {movie.spoken_languages.map(lang => lang.english_name).join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    Box Office
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Budget:</span>
                      <span className="text-gray-900 dark:text-white">{movie.budget ? formatMoney(movie.budget) : 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Revenue:</span>
                      <span className="text-gray-900 dark:text-white">{movie.revenue ? formatMoney(movie.revenue) : 'N/A'}</span>
                    </div>

                    {movie.budget && movie.revenue && movie.revenue > 0 && (
                      <div className="flex justify-between items-start">
                        <span className="text-gray-600 dark:text-gray-400 font-medium">Profit:</span>
                        <span className={`font-semibold ${movie.revenue - movie.budget > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatMoney(movie.revenue - movie.budget)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Production Information */}
                {(movie.production_companies?.length > 0 || movie.production_countries?.length > 0) && (
                  <div className="space-y-4 lg:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                      Production
                    </h3>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {movie.production_companies && movie.production_companies.length > 0 && (
                        <div>
                          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Production Companies</h4>
                          <div className="space-y-2">
                            {movie.production_companies.map((company) => (
                              <div key={company.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                {company.logo_path && (
                                  <img
                                    src={getImageUrl(company.logo_path, 'w200')}
                                    alt={company.name}
                                    className="w-8 h-8 object-contain"
                                  />
                                )}
                                <span className="text-gray-900 dark:text-white text-sm">{company.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {movie.production_countries && movie.production_countries.length > 0 && (
                        <div>
                          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Production Countries</h4>
                          <div className="flex flex-wrap gap-2">
                            {movie.production_countries.map((country) => (
                              <span
                                key={country.iso_3166_1}
                                className="px-3 py-1 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white rounded-full text-sm"
                              >
                                {country.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Rate "{movie.title}"
            </h3>
            
            <div className="flex justify-center space-x-2 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                <button
                  key={rating}
                  onClick={() => handleRating(rating)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors duration-200 ${
                    userRating >= rating
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-yellow-200 dark:hover:bg-yellow-600'
                  }`}
                >
                  {rating}
                </button>
              ))}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowRatingModal(false)}
                className="flex-1 py-3 px-4 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
              
              {userRating && (
                <button
                  onClick={() => handleRating(null)}
                  className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors duration-200"
                >
                  Remove Rating
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Collection Modal */}
      {showCollectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Add to Collection
            </h3>
            
            {collections && collections.length > 0 ? (
              <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                {collections.map((collection) => (
                  <button
                    key={collection.id}
                    onClick={() => handleAddToCollection(collection.id)}
                    className="w-full text-left p-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{collection.name}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {collection.movies?.length || 0} movies
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-center py-4">
                No collections available. Create a collection first to add movies to it.
              </p>
            )}

            <button
              onClick={() => setShowCollectionModal(false)}
              className="w-full py-3 px-4 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovieDetails;